import { cleanPayload } from '@/helpers/misc';
import type { DefaultFilters, EntityService, ExportResponse, ExtendedParameters, HeaderData, ListResponse } from '@/types/api';
import axios, { type AxiosResponse } from 'axios';
import { BaseService } from './baseService';
import { useNotificationStore } from '@/stores/notificationStore';

export abstract class ApiClient<EntityType extends object, CreateType extends object>
  extends BaseService
  implements EntityService<EntityType, CreateType> {

  public identifier: keyof CreateType | keyof EntityType;

  public defaultFilters: DefaultFilters = {
    page: 1,
    pageSize: 10,
  }

  constructor(basePath: string, identifier: keyof EntityType | keyof CreateType) {
    super(basePath);
    this.identifier = identifier;
  }

  getEncodedIdentifier(identifier: string | number): string {
    if (Number.isNaN(parseFloat(identifier as string))) {
      return encodeURIComponent(identifier);
    }
    return identifier as string;
  }

  async getFilteredList({
    filter = {},
    signal,
    pathOverride,
    method,
  }: ExtendedParameters): Promise<ListResponse<EntityType>> {
    try {
      if (method === 'GET') {
        const response = await this.apiClient.get<ListResponse<EntityType>>(
          pathOverride || '/extended',
          {
            params: {
              ...this.defaultFilters,
              ...filter,
              filterList: [
                ...(this.defaultFilters.filterList || []),
                ...(filter.filterList || [])
              ],
              distinct: true
            },
            signal,
          },
        )
        return response.data
      }
      const response = await this.apiClient.post<ListResponse<EntityType>>(
        pathOverride || '/extended',
        {
          ...this.defaultFilters,
          ...filter,
          filterList: [
            ...(this.defaultFilters.filterList || []),
            ...(filter.filterList || [])
          ],
          distinct: true
        },
        { signal },
      )
      return response.data
    } catch (error) {
      console.log(error);
      if (await this.checkApiHealth()) {
        return Promise.reject(new Error('Server is not available.'))
      }

      throw error
    }
  }

  async getExport(filter: DefaultFilters = {}, headerDataList: HeaderData, signal?: AbortSignal, pathOverride?: string): Promise<AxiosResponse<ExportResponse<EntityType>>> {
    try {
      return this.apiClient.get<ExportResponse<EntityType>>(pathOverride || '/extended', {
        params: {
          ...this.defaultFilters,
          ...filter,
          page: 1,
          pageSize: 3000,
          isReport: true,
          ExportHeaderData: JSON.stringify(headerDataList),
        },
        signal,
      })
    } catch (error) {
      if (!await this.checkApiHealth()) {
        return Promise.reject(new Error('Server nicht erreichbar'))
      }

      throw error;
    }
  }

  async getByIdentifier(identifier: string | number, includeRelated?: boolean, signal?: AbortSignal): Promise<EntityType> {
    try {

      const response = await this.apiClient.get<EntityType>(`/${this.getEncodedIdentifier(identifier)}?includeRelated=${!!includeRelated}`, { signal })
      if ((response.data as unknown as EntityType[]).length) return (response.data as unknown as EntityType[])[0];
      return response.data
    } catch (error) {
      if (!await this.checkApiHealth()) {
        return Promise.reject(new Error('Server nicht erreichbar'))
      }

      throw error
    }
  }

  async getVersionsByIdentifier(identifier: string, filter: DefaultFilters = {}, signal?: AbortSignal): Promise<ListResponse<EntityType>> {
    try {
      const response = await this.apiClient.get<ListResponse<EntityType>>(`/versions/${this.getEncodedIdentifier(identifier)}`, {
        signal,
        params: {
          page: 1,
          pageSize: 1000,
          ...filter,
        }
      })
      return response.data
    } catch (error) {
      if (!await this.checkApiHealth()) {
        return Promise.reject(new Error('Server nicht erreichbar'))
      }

      throw error
    }
  }

  async create(record: CreateType): Promise<EntityType> {
    try {
      const response = await this.apiClient.post<EntityType>(``, cleanPayload(record))
      return response.data
    } catch (error) {
      console.log(error);
      if (!await this.checkApiHealth()) {
        return Promise.reject(new Error('Server nicht erreichbar'))
      }

      throw error
    }
  }

  async update(record: CreateType): Promise<EntityType> {
    try {
      const headers = this.getHeaders()
      const response = await this.apiClient.put<EntityType>(`/${this.getEncodedIdentifier(record[this.identifier as keyof CreateType] as string | number)}`, cleanPayload(record), { headers })
      return response.data
    } catch (error) {
      console.log(error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 409) {
          throw new Error('common.identifierConflict')
        }
        if (error.response?.status === 404) {
          throw new Error('common.notfound')
        }
        if (!await this.checkApiHealth()) {
          return Promise.reject(new Error('Server nicht erreichbar'))
        }
      }
      throw error
    }
  }

  async delete(number: string): Promise<void> {
    try {
      await this.apiClient.delete(`/${this.getEncodedIdentifier(number)}`)
    } catch (error) {
      if (!await this.checkApiHealth()) {
        return Promise.reject(new Error('Server nicht erreichbar'))
      }

      throw error
    }
  }

  async checkApiHealth() {
    try {
      const response = await this.apiClient.get("../../health");

      return response.status === 200;
    } catch (error) {
      if (error instanceof Error) {
        console.error("Health check failed:", error.message);
      } else {
        console.error("Health check failed", error);
      }

      const notificationStore = useNotificationStore();
      if (!notificationStore.notifications.some(n => n.text === 'Server is not available.')) {
        notificationStore.addError('Server is not available.');
      }
      return false;
    }
  }
}
